import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAppLaunch } from "@/components/landing/AppLaunch";
import { LanguageSwitcher } from "@/components/landing/LanguageSwitcher";
import { useLang } from "@/i18n/LanguageContext";
import { LOGO_GREEN } from "@/lib/assets";

export const Logo = () => (
  <span className="flex items-center gap-2.5 font-outfit font-extrabold text-2xl tracking-tight text-white">
    <img src={LOGO_GREEN} alt="Jos'FIT" className="h-10 w-10 rounded-xl" />
    <span>
      Jos'<span className="text-emerald-400">FIT</span>
    </span>
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { launchApp } = useAppLaunch();
  const { t } = useLang();

  const links = [
    { label: t.nav.features, href: "#fonctionnalites" },
    { label: t.nav.preview, href: "#apercu" },
    { label: t.nav.demo, href: "#demo" },
    { label: t.nav.byok, href: "#byok" },
    { label: t.nav.pricing, href: "#tarifs" },
    { label: t.nav.faq, href: "#faq" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-[var(--banner-h,0px)] inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#080A09]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
        <a href="#top" data-testid="navbar-logo">
          <Logo />
        </a>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              className="text-sm font-medium text-slate-400 hover:text-white transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <button
            type="button"
            onClick={launchApp}
            data-testid="navbar-cta"
            className="inline-flex items-center rounded-full bg-emerald-400 px-6 py-2.5 text-sm font-bold text-emerald-950 hover:bg-emerald-300 transition-colors duration-200 shadow-lg shadow-emerald-500/25"
          >
            {t.nav.launch}
          </button>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="navbar-menu-toggle"
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#080A09]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.href.replace("#", "")}`}
                  className="text-base font-medium text-slate-300 hover:text-emerald-400"
                >
                  {l.label}
                </a>
              ))}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  launchApp();
                }}
                data-testid="navbar-mobile-cta"
                className="mt-2 inline-flex justify-center rounded-full bg-emerald-400 px-6 py-3 text-sm font-bold text-emerald-950"
              >
                {t.nav.launch}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
